import * as React from "react";
import * as Immutable from "immutable";
import { useState } from "react";
import { Formik, Form } from "formik";
import styled, { css } from "styled-components";
import { useTranslation } from "react-i18next";
import { Alert, Col, Row, Input } from "components/bootstrap";
import { Headline } from "components/common/Section/SectionComponent";
import PaginatedItem from "components/common/PaginatedItemOverview/PaginatedItem";
import RolesSelector from "components/permissions/RolesSelector";
import { FormSubmit } from "components/common";
import useHistory from "routing/useHistory";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import EntityCreateShareFormGroup from "components/permissions/EntityCreateShareFormGroup";
import CurrentUser from "logic/users/CurrentUser";
import SecurityRoutes from "security/Routes";
import LicenseCheck from "../../../license/LicenseCheck";
import NameFormGroup from "../form/NameFormGroup";
import DescriptionFormGroup from "../form/DescriptionFormGroup";
import Team from "../../logic/Team";
import TeamsDomain from "../../domainActions/TeamsDomain";
import UsersSelector from "../team-edit/UsersSelector";
const NoSelectedItem = styled.div`
  padding-top: 7px;
`;
const FormSubmitRow = styled(Row)(
  ({ theme }) => css`
    margin-top: ${theme.spacings.sm};
  `
);
const _onSubmit = ({ name, description, share_request }, users, roles, setSubmitError, history) => {
  setSubmitError(null);
  const newTeam = Team.builder().name(name).users(users).roles(roles).description(description).build();
  return TeamsDomain.create(newTeam, share_request).then(
    () => {
      CurrentUser.reload();
      history.push(SecurityRoutes.TEAMS.OVERVIEW);
    },
    (error) => setSubmitError(error)
  );
};
const TeamCreate = () => {
  const { t } = useTranslation("enterprise_security");
  const [submitError, setSubmitError] = useState();
  const [team, setTeam] = useState(Team.empty());
  const [selectedUsers, setSelectedUsers] = useState(Immutable.Set());
  const [selectedRoles, setSelectedRoles] = useState(Immutable.Set());
  const history = useHistory();
  const sendTelemetry = useSendTelemetry("teams-overview");
  const _handleCancel = () => history.push(SecurityRoutes.TEAMS.OVERVIEW);
  const _onAssignUser = (users) => {
    setSelectedUsers(selectedUsers.union(users));
    return Promise.resolve(
      setTeam(
        team.toBuilder().users(team.users.union(users.map((u) => u.id))).build()
      )
    );
  };
  const _onAssignRole = (roles) => {
    setSelectedRoles(selectedRoles.union(roles));
    return Promise.resolve(
      setTeam(
        team.toBuilder().roles(team.roles.union(roles.map((r) => r.id))).build()
      )
    );
  };
  const _onUnassignUser = (user) => {
    setSelectedUsers(selectedUsers.remove(user));
    setTeam(team.toBuilder().users(team.users.remove(user.name)).build());
  };
  const _onUnassignRole = (role) => {
    setSelectedRoles(selectedRoles.remove(role));
    setTeam(team.toBuilder().roles(team.roles.remove(role.id)).build());
  };
  return /* @__PURE__ */ React.createElement(LicenseCheck, { featureName: "teams", displayWarningContainer: true }, ({ licenseIsValid }) => {
    if (!licenseIsValid) {
      return null;
    }
    return /* @__PURE__ */ React.createElement(Row, { className: "content" }, /* @__PURE__ */ React.createElement(Col, { lg: 8 }, /* @__PURE__ */ React.createElement(
      Formik,
      {
        onSubmit: (data) => {
          sendTelemetry(TELEMETRY_EVENT_TYPE.TEAM.CREATED, {
            app_action_value: "team-create"
          });
          _onSubmit(data, team.users, team.roles, setSubmitError, history);
        },
        initialValues: { name: void 0, description: void 0, share_request: void 0 }
      },
      ({ isSubmitting, isValid, setFieldValue }) => {
        var _a, _b;
        return /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Headline, null, t("team_create.profile")), /* @__PURE__ */ React.createElement(NameFormGroup, null), /* @__PURE__ */ React.createElement(DescriptionFormGroup, null)), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Headline, null, t("team_create.users")), /* @__PURE__ */ React.createElement(
          Input,
          {
            id: "users-selector-input",
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9",
            label: t("team_create.assign_users")
          },
          /* @__PURE__ */ React.createElement(UsersSelector, { onSubmit: _onAssignUser, team })
        ), /* @__PURE__ */ React.createElement(
          Input,
          {
            id: "selected-users-overview",
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9",
            label: t("team_create.selected_users")
          },
          /* @__PURE__ */ React.createElement(React.Fragment, null, selectedUsers.toArray().map((user) => /* @__PURE__ */ React.createElement(PaginatedItem, { item: user, onDeleteItem: (data) => _onUnassignUser(data), key: user.name })), selectedUsers.size <= 0 && /* @__PURE__ */ React.createElement(NoSelectedItem, null, t("team_create.no_selected_users")))
        )), /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(Headline, null, t("team_create.roles")), /* @__PURE__ */ React.createElement(
          Input,
          {
            id: "roles-selector-input",
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9",
            label: t("team_create.assign_roles")
          },
          /* @__PURE__ */ React.createElement(RolesSelector, { onSubmit: _onAssignRole, assignedRolesIds: team.roles, submitOnSelect: true })
        ), /* @__PURE__ */ React.createElement(
          Input,
          {
            id: "selected-users-overview",
            labelClassName: "col-sm-3",
            wrapperClassName: "col-sm-9",
            label: t("team_create.selected_roles")
          },
          /* @__PURE__ */ React.createElement(React.Fragment, null, selectedRoles.toArray().map((role) => /* @__PURE__ */ React.createElement(PaginatedItem, { item: role, onDeleteItem: (data) => _onUnassignRole(data), key: role.name })), selectedRoles.size <= 0 && /* @__PURE__ */ React.createElement(NoSelectedItem, null, t("team_create.no_selected_roles")))
        )), /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { xs: 3 }, /* @__PURE__ */ React.createElement(Headline, null, t("team_create.sharing"))), /* @__PURE__ */ React.createElement(Col, { xs: 9 }, /* @__PURE__ */ React.createElement(
          EntityCreateShareFormGroup,
          {
            description: t("team_create.share_description"),
            entityType: "team",
            onSetEntityShare: (payload) => setFieldValue("share_request", payload)
          }
        ))), submitError && /* @__PURE__ */ React.createElement(Row, null, /* @__PURE__ */ React.createElement(Col, { xs: 9, xsOffset: 3 }, /* @__PURE__ */ React.createElement(Alert, { bsStyle: "danger", title: t("team_create.failed_to_create") }, (_b = (_a = submitError == null ? void 0 : submitError.additional) == null ? void 0 : _a.res) == null ? void 0 : _b.text))), /* @__PURE__ */ React.createElement(FormSubmitRow, null, /* @__PURE__ */ React.createElement(Col, { md: 9, mdOffset: 3 }, /* @__PURE__ */ React.createElement(
          FormSubmit,
          {
            disabledSubmit: !isValid,
            submitButtonText: t("team_create.create_team"),
            submitLoadingText: t("team_create.creating_team"),
            isSubmitting,
            isAsyncSubmit: true,
            onCancel: _handleCancel
          }
        ))));
      }
    )));
  });
};
export default TeamCreate;
